// Registro central de módulos/paneles y permisos por rol.

export const MODULE_LABELS = {
  inicio: "Inicio (Dashboard)",
  mesas: "Pedidos en Mesa",
  pedido: "Generar Pedido",
  caja: "Caja",
  pedidos: "Pedidos",
  historial: "Historial",
  facturacion: "Facturación electrónica (CFDI)",
  clientes: "Clientes",
  conciliacion: "Conciliación Plataformas",
  "propinas-pendientes": "Propinas pendientes",
  repartidores: "Control de Repartidores",
  cajas: "Cajas de cajeros",
  multisucursal: "Mis Negocios (Multisucursal)",
  "pedidos-web": "Pedidos web / app (tienda en línea y QR)",
  plataformas: "Plataformas (Uber Eats, DiDi, Rappi)",
  bancos: "Bancos y Cajas",
  kds: "Cocina (KDS)",
  productos: "Productos",
  inventario: "Inventario",
  reportes: "Reportes",
  propinas: "Propinas",
  gastos: "Gastos",
  "gastos-fijos": "Gastos Fijos",
  nomina: "Nómina",
  usuarios: "Usuarios",
  configuracion: "Configuración",
  tablet: "Modo Tablet",
  comandera: "Comandera (Mesero)",
  suscripcion: "Mi Suscripción",
};

export const MODULE_ROUTE = {
  inicio: "/app",
  mesas: "/app/mesas",
  pedido: "/app/pedido",
  caja: "/app/caja",
  pedidos: "/app/pedidos",
  historial: "/app/historial",
  facturacion: "/app/facturacion",
  clientes: "/app/clientes",
  conciliacion: "/app/conciliacion",
  "propinas-pendientes": "/app/propinas-pendientes",
  repartidores: "/app/repartidores",
  cajas: "/app/cajas",
  multisucursal: "/app/negocios",
  bancos: "/app/bancos",
  kds: "/app/kds",
  productos: "/app/productos",
  inventario: "/app/inventario",
  reportes: "/app/reportes",
  propinas: "/app/propinas",
  gastos: "/app/gastos",
  "gastos-fijos": "/app/gastos-fijos",
  nomina: "/app/nomina",
  usuarios: "/app/usuarios",
  configuracion: "/app/configuracion",
  tablet: "/app/tablet",
  comandera: "/app/comandera",
  suscripcion: "/app/suscripcion",
};

// Módulos que se pueden asignar a cada rol (casillas visibles al crear/editar).
// admin = null => ve todo y no se puede restringir. repartidor no inicia sesión.
export const ROLE_ELIGIBLE = {
  admin: null,
  manager: [
    "inicio", "pedido", "caja", "cajas", "multisucursal", "pedidos", "clientes", "historial", "facturacion", "repartidores",
    "bancos", "kds", "productos", "inventario", "reportes", "gastos",
    "gastos-fijos", "nomina", "usuarios", "configuracion", "tablet", "comandera", "propinas", "conciliacion", "propinas-pendientes",
  ],
  cashier: ["inicio", "pedido", "caja", "pedidos", "clientes", "historial", "facturacion", "repartidores", "bancos", "comandera", "propinas", "propinas-pendientes"],
  waiter: ["mesas", "pedido", "pedidos", "comandera", "propinas-pendientes"],
  kitchen: ["kds"],
  repartidor: [],
};

// Módulos visibles por defecto cuando el usuario no tiene permisos explícitos.
export const ROLE_DEFAULTS = {
  admin: null,
  manager: ROLE_ELIGIBLE.manager,
  cashier: ROLE_ELIGIBLE.cashier,
  waiter: ["mesas", "pedidos", "comandera", "propinas-pendientes"],
  kitchen: ["kds"],
  repartidor: [],
};

// Devuelve la lista de módulos permitidos para el usuario, o null (= todos, admin).
// También aplica el plan SaaS contratado (user.plan_modules).
const CORE_MODULES = ["inicio", "suscripcion"];
const NEVER_HIDE = ["inicio", "configuracion", "suscripcion"];
export const HIDEABLE_MODULES = Object.keys(MODULE_LABELS).filter((k) => !NEVER_HIDE.includes(k));
export const isHiddenModule = (user, key) => !NEVER_HIDE.includes(key) && (user?.hidden_modules || []).includes(key);

export function modulesForUser(user) {
  if (!user) return [];
  if (user.role === "superadmin") return null;
  const mods = planModules(user);
  if (!(user.hidden_modules || []).length) return mods;
  return (mods === null ? Object.keys(MODULE_LABELS) : mods).filter((m) => !isHiddenModule(user, m));
}

function planModules(user) {
  const base = roleModules(user);
  if (!Array.isArray(user.plan_modules)) return base;
  const allowed = [...CORE_MODULES, ...user.plan_modules];
  return base === null ? allowed : base.filter((m) => allowed.includes(m));
}

function roleModules(user) {
  if (user.role === "admin") return null;
  const eligible = ROLE_ELIGIBLE[user.role] || [];
  if (user.allowed_modules && user.allowed_modules.length) {
    return user.allowed_modules.filter((m) => eligible.includes(m));
  }
  return ROLE_DEFAULTS[user.role] || [];
}

export function canSeeModule(user, key) {
  const mods = modulesForUser(user);
  if (mods === null) return true;
  return mods.includes(key);
}

// Módulos que el rol permite pero el plan SaaS no incluye (se muestran con candado).
export const PLAN_GATED = [
  "pedido", "caja", "pedidos", "historial", "productos", "usuarios", "configuracion", "mesas", "comandera", "kds",
  "inventario", "clientes", "reportes", "propinas", "propinas-pendientes", "repartidores", "cajas", "conciliacion",
  "bancos", "gastos", "gastos-fijos", "nomina", "tablet", "multisucursal", "pedidos-web", "plataformas", "facturacion",
];

export function planLocked(user, key) {
  if (!user || user.role === "superadmin" || !Array.isArray(user.plan_modules)) return false;
  if (isHiddenModule(user, key)) return false;
  if (!PLAN_GATED.includes(key) || user.plan_modules.includes(key)) return false;
  const base = roleModules(user);
  return base === null || base.includes(key);
}
